import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { LeadFormBanner } from '../components/LeadFormBanner';
import { formatDateTime } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import {
  MessageCircle, Loader2, Search, Send, RefreshCw, User,
  Inbox, ChevronLeft, Settings, AtSign, X, CheckCircle2,
  AlertTriangle, Phone, Clock, Tag, StickyNote, Link2, UserCheck, UserPlus, Download,
  Paperclip, Smile, FileText, Play, MailPlus, Reply, Megaphone,
  Filter, Palette, Plus, Trash2, Pencil, Check, Smartphone, CheckSquare, Zap, AlarmClock, AlarmClockOff, MoreVertical, Bell, BellRing, Bold, Italic, Strikethrough, Code,
} from 'lucide-react';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { LinkCustomerDialog } from '../components/WhatsAppLinkCustomer';
import { WhatsAppComposeDialog } from '../components/WhatsAppComposeDialog';
import { WhatsAppBroadcastDialog } from '../components/WhatsAppBroadcastDialog';

const MEDIA_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/media`;

// Varied WhatsApp-style avatar palette (includes Helios blues).
const AVATAR_COLORS = ['#2563eb', '#0ea5e9', '#0d9488', '#059669', '#65a30d', '#d97706', '#dc2626', '#db2777', '#7c3aed', '#4f46e5', '#0891b2', '#ca8a04'];
const CATEGORY_COLORS = ['#2563eb', '#0ea5e9', '#0d9488', '#059669', '#65a30d', '#d97706', '#dc2626', '#db2777', '#7c3aed', '#4f46e5', '#0891b2', '#ca8a04', '#64748b'];

function avatarColor(seed) {
  const s = String(seed || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// WhatsApp-style list timestamp: time if the message is from today, else the date.
function waListTime(dateString) {
  if (!dateString) return '';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  const tz = 'Europe/London';
  const dayStr = (x) => x.toLocaleDateString('en-GB', { timeZone: tz });
  const now = new Date();
  if (dayStr(d) === dayStr(now)) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz });
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString('en-GB', sameYear
    ? { day: 'numeric', month: 'short', timeZone: tz }
    : { day: 'numeric', month: 'short', year: '2-digit', timeZone: tz });
}

// Render WhatsApp markup (*bold* _italic_ ~strike~ ```mono```) to safe HTML for the inline preview.
function waMarkupHtml(text) {
  let s = String(text || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Protect monospace spans so their contents render literally (no nested styling).
  const mono = [];
  s = s.replace(/```([\s\S]+?)```/g, (_, inner) => {
    mono.push(inner);
    return `\u0000${mono.length - 1}\u0000`;
  });
  // Bold → italic → strike, applied on the full string so they nest inside one another.
  s = s.replace(/\*(?=\S)([\s\S]*?\S)\*/g, '<strong style="font-weight:600">$1</strong>');
  s = s.replace(/_(?=\S)([\s\S]*?\S)_/g, '<em>$1</em>');
  s = s.replace(/~(?=\S)([\s\S]*?\S)~/g, '<del>$1</del>');
  // Restore protected monospace spans.
  s = s.replace(/\u0000(\d+)\u0000/g, (_, i) =>
    `<code style="font-family:monospace;background:#e2e8f0;border-radius:3px;padding:0 3px;font-size:0.9em">${mono[Number(i)]}</code>`);
  return s;
}

// Convert a base64url VAPID key into the Uint8Array the Push API expects.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

// WhatsApp-style circular avatar: coloured initials, generic person icon when no name.
function InitialsAvatar({ name, phone, size = 44, className = '' }) {
  const display = name && name !== phone ? name : '';
  const ini = initialsOf(display);
  const color = avatarColor(display || phone || '');
  return (
    <div
      className={`rounded-full flex items-center justify-center flex-shrink-0 text-white font-semibold ${className}`}
      style={{ width: size, height: size, backgroundColor: color, fontSize: Math.round(size * 0.38) }}
      data-testid="wa-avatar"
    >
      {ini || <User style={{ width: size * 0.5, height: size * 0.5 }} />}
    </div>
  );
}

// Live, ticking countdown for WhatsApp's 24-hour reply window (from the
// customer's last inbound message). Shown as a strip under the chat header.
function ReplyWindowTimer({ lastInboundAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!lastInboundAt) return null;
  const start = new Date(lastInboundAt).getTime();
  if (isNaN(start)) return null;
  const end = start + 24 * 3600 * 1000;
  const remaining = Math.floor((end - now) / 1000);
  const deadline = new Date(end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (remaining <= 0) {
    return (
      <div className="flex items-center gap-2 px-4 py-1.5 text-xs font-medium border-b bg-rose-50 text-rose-700 border-rose-200" data-testid="wa-reply-timer">
        <AlarmClockOff className="w-3.5 h-3.5 flex-shrink-0" />
        <span>Reply window closed — send an approved template to message this customer.</span>
      </div>
    );
  }
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const clock = h > 0
    ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
    : `${m}m ${String(s).padStart(2, '0')}s`;
  const cls = remaining <= 2 * 3600
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : remaining <= 6 * 3600
      ? 'bg-amber-50 text-amber-700 border-amber-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <div className={`flex items-center gap-2 px-4 py-1.5 text-xs font-medium border-b ${cls}`} data-testid="wa-reply-timer">
      <AlarmClock className="w-3.5 h-3.5 flex-shrink-0" />
      <span data-testid="wa-reply-timer-clock">{clock} left to reply</span>
      <span className="opacity-70">· window closes {deadline}</span>
    </div>
  );
}

// ─── Internal Note with Tags ───
function InternalNote({ note }) {
  return (
    <div className="mx-4 my-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`internal-note-${note.id}`}>
      <div className="flex items-start gap-2">
        <StickyNote className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-amber-800 whitespace-pre-wrap">
            {note.text.split(/(@\w[\w\s]*)/g).map((part, i) =>
              part.startsWith('@') ? (
                <span key={i} className="bg-amber-200 text-amber-900 rounded px-1 py-0.5 font-medium text-[11px]">
                  {part}
                </span>
              ) : part
            )}
          </p>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-[10px] text-amber-600">{note.created_by}</span>
            <span className="text-[10px] text-amber-500">{note.created_at ? formatDateTime(note.created_at) : ''}</span>
            {note.tagged_users?.length > 0 && (
              <div className="flex items-center gap-1">
                <Tag className="w-2.5 h-2.5 text-amber-500" />
                {note.tagged_users.map((u, i) => (
                  <span key={i} className="text-[10px] text-amber-700 font-medium">{u.name}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Message Bubble ───
function MessageBubble({ msg, onReply }) {
  const isOutbound = msg.direction === 'outbound';
  const kind = msg.media_kind;
  const mediaUrl = msg.media_token ? `${MEDIA_BASE}/${msg.media_token}` : null;
  const caption = msg.media_caption || (kind ? msg.text : '');
  return (
    <div className={`group flex items-center gap-1 ${isOutbound ? 'justify-end' : 'justify-start'} mb-2 px-4`}>
      {isOutbound && onReply && (
        <button onClick={() => onReply(msg)} data-testid={`wa-reply-btn-${msg.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-emerald-600 p-1"
          title="Reply to this message">
          <Reply className="w-4 h-4" />
        </button>
      )}
      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${
        isOutbound
          ? 'bg-emerald-600 text-white rounded-br-md'
          : 'bg-white border border-slate-200 rounded-bl-md'
      }`}>
        {msg.reply_to && (
          <div className={`mb-1 rounded-md px-2 py-1 border-l-2 text-xs ${
            isOutbound ? 'bg-emerald-700/40 border-emerald-200' : 'bg-slate-100 border-slate-400'}`}>
            <span className={`block font-medium ${isOutbound ? 'text-emerald-100' : 'text-slate-500'}`}>
              {msg.reply_direction === 'outbound' ? 'You' : 'Them'}
            </span>
            <span className={`block truncate ${isOutbound ? 'text-emerald-50' : 'text-slate-600'}`}>
              {msg.reply_preview || 'message'}
            </span>
          </div>
        )}
        {mediaUrl && kind === 'image' && (
          <a href={mediaUrl} target="_blank" rel="noopener noreferrer" data-testid={`wa-media-image-${msg.id}`}>
            <img src={mediaUrl} alt={msg.media_filename || 'image'} loading="lazy"
              className="rounded-lg max-w-full max-h-64 object-cover mb-1" />
          </a>
        )}
        {mediaUrl && kind === 'video' && (
          <video src={mediaUrl} controls className="rounded-lg max-w-full max-h-64 mb-1" data-testid={`wa-media-video-${msg.id}`} />
        )}
        {mediaUrl && kind === 'audio' && (
          <audio src={mediaUrl} controls className="mb-1 max-w-full" data-testid={`wa-media-audio-${msg.id}`} />
        )}
        {mediaUrl && (kind === 'document' || kind === 'sticker') && (
          <a href={`${mediaUrl}?download=true`} target="_blank" rel="noopener noreferrer"
            data-testid={`wa-media-doc-${msg.id}`}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 mb-1 ${isOutbound ? 'bg-emerald-700/50' : 'bg-slate-100'}`}>
            <FileText className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm truncate underline">{msg.media_filename || 'Document'}</span>
            <Download className="w-4 h-4 flex-shrink-0 ml-auto" />
          </a>
        )}
        {caption && <p className="text-sm whitespace-pre-wrap">{caption}</p>}
        {!kind && msg.text && <p className="text-sm whitespace-pre-wrap">{msg.text}</p>}
        <p className={`text-[10px] mt-1 ${isOutbound ? 'text-emerald-200' : 'text-slate-400'}`}>
          {msg.timestamp ? formatDateTime(msg.timestamp) : ''}
          {msg.sent_by && <span className="ml-1">by {msg.sent_by}</span>}
        </p>
      </div>
      {!isOutbound && onReply && (
        <button onClick={() => onReply(msg)} data-testid={`wa-reply-btn-${msg.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-emerald-600 p-1"
          title="Reply to this message">
          <Reply className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// ─── Conversation List Item (WhatsApp-Business style) ───
function ConversationItem({ conv, isActive, onClick, categories = [], selectMode = false, selected = false, onToggleSelect }) {
  const cat = conv.category_id ? categories.find((c) => c.id === conv.category_id) : null;
  const name = conv.customer_name || conv.contact_name || conv.phone;
  const unread = conv.unread_count > 0;
  const handleClick = () => { if (selectMode) onToggleSelect?.(conv.id); else onClick?.(); };
  return (
    <button
      data-testid={`wa-conv-${conv.id}`}
      onClick={handleClick}
      className={`w-full text-left px-3 sm:px-4 py-3 min-h-[76px] border-b border-border/50 transition-colors hover:bg-muted/50 ${
        isActive && !selectMode ? 'bg-emerald-50 border-l-2 border-l-emerald-600' : ''
      } ${selected ? 'bg-emerald-50/70' : ''}`}
    >
      <div className="flex items-center gap-3">
        {selectMode && (
          <Checkbox checked={selected} className="flex-shrink-0 pointer-events-none" data-testid={`wa-conv-select-${conv.id}`} />
        )}
        <div className="relative flex-shrink-0">
          <InitialsAvatar name={name} phone={conv.phone} size={48} />
          {unread && (
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white shadow-sm" data-testid={`wa-unread-dot-${conv.id}`} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className={`flex-1 min-w-0 truncate text-[15px] sm:text-sm ${unread ? 'font-semibold text-slate-900' : 'font-medium'}`}>{name}</span>
            <span className={`flex-shrink-0 whitespace-nowrap text-[11px] ${unread ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`} data-testid={`wa-conv-time-${conv.id}`}>
              {waListTime(conv.last_message_time || conv.last_inbound_at || conv.updated_at || conv.created_at)}
            </span>
          </div>
          <div className="flex items-start justify-between gap-2 mt-0.5">
            <p className={`flex-1 min-w-0 line-clamp-2 break-words text-[13px] sm:text-xs ${unread ? 'text-slate-700 font-medium' : 'text-muted-foreground'}`}>
              {conv.last_message || 'No messages'}
            </p>
            {unread && (
              <Badge className="ml-1 mt-0.5 h-5 min-w-[20px] justify-center rounded-full bg-emerald-600 hover:bg-emerald-600 text-white text-[11px] flex-shrink-0">
                {conv.unread_count}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {cat && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: `${cat.color}1a`, color: cat.color }}
                data-testid={`wa-conv-category-${conv.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cat.color }} />
                {cat.label}
              </span>
            )}
            {conv.customer_id ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full" data-testid={`wa-conv-linked-${conv.id}`}>
                <UserCheck className="w-3 h-3" /> {conv.customer_name || 'Linked'}
              </span>
            ) : conv.match_suggestion ? (
              <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-full">
                <Link2 className="w-3 h-3" /> {conv.match_suggestion.name}?
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">Unlinked</span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Tag User Popover ───
function TagUserPopover({ users, onSelect, children }) {
  const [search, setSearch] = useState('');
  const filtered = users.filter((u) =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <Input
          placeholder="Search team members..."
          className="h-8 text-xs mb-2"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="tag-user-search"
        />
        <ScrollArea className="max-h-40">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-2">No users found</p>
          ) : (
            filtered.map((u) => (
              <button
                key={u.id || u.email}
                className="w-full text-left px-2 py-1.5 hover:bg-muted rounded text-sm flex items-center gap-2"
                onClick={() => { onSelect(u); setSearch(''); }}
                data-testid={`tag-option-${u.email}`}
              >
                <User className="w-3.5 h-3.5 text-muted-foreground" />
                <span>{u.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">{u.role || ''}</span>
              </button>
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}


// ─── Main Page ───
export default function WhatsAppBusiness() {
  const [configured, setConfigured] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [internalNotes, setInternalNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const fileInputRef = useRef(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [noteText, setNoteText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [teamUsers, setTeamUsers] = useState([]);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState([]);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [signature, setSignature] = useState(true);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', phone: '' });
  const [savingNewContact, setSavingNewContact] = useState(false);

  const saveNewContact = async () => {
    if (!newContact.name.trim() || !newContact.phone.trim()) {
      toast.error('Enter a name and phone number');
      return;
    }
    setSavingNewContact(true);
    try {
      await api.post('/whatsapp/contacts', { name: newContact.name.trim(), phone: newContact.phone.trim() });
      toast.success('Contact saved — it’ll auto-link when a matching customer is added');
      setAddContactOpen(false);
      setNewContact({ name: '', phone: '' });
      fetchConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save contact');
    } finally { setSavingNewContact(false); }
  };
  const [saveContactOpen, setSaveContactOpen] = useState(false);
  const [contactNameInput, setContactNameInput] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [importing, setImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [catForm, setCatForm] = useState({ id: '', label: '', color: '#2563eb' });
  const [savingCat, setSavingCat] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [iosHelpOpen, setIosHelpOpen] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [quickReplies, setQuickReplies] = useState([]);
  const [qrPopoverOpen, setQrPopoverOpen] = useState(false);
  const [qrManageOpen, setQrManageOpen] = useState(false);
  const [qrForm, setQrForm] = useState({ id: '', title: '', text: '' });
  const [savingQr, setSavingQr] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const mirrorRef = useRef(null);

  const wrapSelection = (marker) => {
    const el = messageInputRef.current;
    const text = messageText;
    let start = text.length;
    let end = text.length;
    if (el && typeof el.selectionStart === 'number') {
      start = el.selectionStart;
      end = el.selectionEnd;
    }
    const sel = text.slice(start, end);
    const next = `${text.slice(0, start)}${marker}${sel}${marker}${text.slice(end)}`;
    setMessageText(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = sel ? end + marker.length * 2 : start + marker.length;
      try { el.setSelectionRange(caret, caret); } catch {}
    });
  };

  useEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    if (messageText) el.style.height = Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.5)) + 'px';
    if (mirrorRef.current) mirrorRef.current.scrollTop = el.scrollTop;
  }, [messageText]);

  const fetchQuickReplies = async () => {
    try {
      const { data } = await api.get('/whatsapp/quick-replies');
      setQuickReplies(data.quick_replies || []);
    } catch {}
  };

  const insertQuickReply = (qr) => {
    const first = (activeConv?.customer_name || activeConv?.contact_name || '').trim().split(/\s+/)[0] || 'there';
    const text = (qr.text || '').replace(/\{name\}/gi, first);
    setMessageText((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text));
    setQrPopoverOpen(false);
  };

  const saveQuickReply = async () => {
    const title = qrForm.title.trim();
    const text = qrForm.text.trim();
    if (!title || !text) { toast.error('Enter a title and a message'); return; }
    setSavingQr(true);
    try {
      if (qrForm.id) {
        await api.put(`/whatsapp/quick-replies/${qrForm.id}`, { title, text });
      } else {
        await api.post('/whatsapp/quick-replies', { title, text });
      }
      setQrForm({ id: '', title: '', text: '' });
      fetchQuickReplies();
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save quick reply');
    } finally { setSavingQr(false); }
  };

  const deleteQuickReply = async (id) => {
    try {
      await api.delete(`/whatsapp/quick-replies/${id}`);
      if (qrForm.id === id) setQrForm({ id: '', title: '', text: '' });
      fetchQuickReplies();
    } catch { toast.error('Could not delete quick reply'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const applyBulkCategory = async (value) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) { toast.error('Select at least one chat first'); return; }
    const categoryId = value === '__none__' ? '' : value;
    try {
      await api.post('/whatsapp/conversations/bulk-category', { conversation_ids: ids, category_id: categoryId });
      fetchConversations();
      setSelectMode(false);
      setSelectedIds(new Set());
      toast.success(`Updated ${ids.length} chat${ids.length > 1 ? 's' : ''}`);
    } catch { toast.error('Could not update chats'); }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/whatsapp/categories');
      setCategories(data.categories || []);
    } catch {}
  };

  const saveCategory = async () => {
    const label = catForm.label.trim();
    if (!label) { toast.error('Enter a category name'); return; }
    setSavingCat(true);
    try {
      if (catForm.id) {
        await api.put(`/whatsapp/categories/${catForm.id}`, { label, color: catForm.color });
      } else {
        await api.post('/whatsapp/categories', { label, color: catForm.color });
      }
      setCatForm({ id: '', label: '', color: '#2563eb' });
      fetchCategories();
      toast.success('Saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save category');
    } finally { setSavingCat(false); }
  };

  const deleteCategory = async (id) => {
    try {
      await api.delete(`/whatsapp/categories/${id}`);
      if (catForm.id === id) setCatForm({ id: '', label: '', color: '#2563eb' });
      fetchCategories();
      fetchConversations();
    } catch { toast.error('Could not delete category'); }
  };

  const setConversationCategory = async (categoryId) => {
    if (!activeConv) return;
    try {
      await api.post(`/whatsapp/conversations/${activeConv.id}/category`, { category_id: categoryId });
      setActiveConv((prev) => (prev ? { ...prev, category_id: categoryId || undefined } : prev));
      setConversations((prev) => prev.map((c) => (c.id === activeConv.id ? { ...c, category_id: categoryId || undefined } : c)));
      toast.success(categoryId ? 'Category set' : 'Category cleared');
    } catch { toast.error('Could not update category'); }
  };

  const installToHome = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch {}
      setDeferredPrompt(null);
      return;
    }
    setIosHelpOpen(true);
  };

  const enablePush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      toast.error('Push notifications are not supported on this device/browser');
      return;
    }
    setPushBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error('Notification permission was not granted');
        return;
      }
      const reg = await navigator.serviceWorker.register('/wa-sw.js');
      await navigator.serviceWorker.ready;
      const { data } = await api.get('/whatsapp/push/vapid-public-key');
      if (!data.publicKey) { toast.error('Push is not configured on the server'); return; }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(data.publicKey),
        });
      }
      const json = sub.toJSON();
      await api.post('/whatsapp/push/subscribe', { endpoint: sub.endpoint, keys: json.keys });
      setPushEnabled(true);
      toast.success('Notifications enabled on this device');
    } catch (e) {
      toast.error('Could not enable notifications');
    } finally { setPushBusy(false); }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.post('/whatsapp/push/unsubscribe', { endpoint: sub.endpoint }).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      setPushEnabled(false);
      toast.success('Notifications turned off on this device');
    } catch { toast.error('Could not turn off notifications'); }
    finally { setPushBusy(false); }
  };

  const sendTestPush = async () => {
    try {
      const { data } = await api.post('/whatsapp/push/test');
      if (data.sent > 0) toast.success('Test notification sent — check your device');
      else toast.info('No device subscribed yet — enable notifications first');
    } catch { toast.error('Could not send a test notification'); }
  };

  const createCustomerFromChat = () => {
    if (!activeConv) return;
    const raw = (activeConv.contact_name && activeConv.contact_name !== activeConv.phone) ? activeConv.contact_name : '';
    const parts = raw.trim().split(/\s+/).filter(Boolean);
    const first_name = parts[0] || '';
    const last_name = parts.slice(1).join(' ') || '';
    const phone = activeConv.phone || '';
    navigate('/customers/new', {
      state: { first_name, last_name, phone, whatsapp_number: phone, from_whatsapp: true },
    });
  };

  // Load team users for tagging
  useEffect(() => {
    api.get('/admin/users').then(({ data }) => {
      setTeamUsers(data.users || data || []);
    }).catch(() => {});

    // Check if WhatsApp is configured
    api.get('/whatsapp/status').then(({ data }) => {
      setConfigured(data.configured || false);
    }).catch(() => {
      setConfigured(false);
    }).finally(() => setLoading(false));

    // Always fetch conversations — inbox should render whenever any
    // conversation exists (e.g. seeded demo data), even before WhatsApp
    // is fully configured.
    fetchConversations();
    fetchCategories();
    fetchQuickReplies();
  }, []);

  // PWA: register a service worker + attach a WhatsApp-scoped manifest so
  // "Add to Home Screen" installs an icon that opens straight into /whatsapp.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/wa-sw.js').catch(() => {});
    }
    let link = document.querySelector('link[rel="manifest"][data-wa]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/wa-manifest.json';
      link.setAttribute('data-wa', 'true');
      document.head.appendChild(link);
    }
    const addMeta = (name, content) => {
      let m = document.querySelector(`meta[name="${name}"][data-wa]`);
      if (!m) { m = document.createElement('meta'); m.name = name; m.setAttribute('data-wa', 'true'); document.head.appendChild(m); }
      m.content = content;
    };
    addMeta('apple-mobile-web-app-capable', 'yes');
    addMeta('apple-mobile-web-app-title', 'Helios WA');
    addMeta('apple-mobile-web-app-status-bar-style', 'default');
    let appleIcon = document.querySelector('link[rel="apple-touch-icon"][data-wa]');
    if (!appleIcon) {
      appleIcon = document.createElement('link');
      appleIcon.rel = 'apple-touch-icon';
      appleIcon.href = '/wa-icon.png';
      appleIcon.setAttribute('data-wa', 'true');
      document.head.appendChild(appleIcon);
    }
    const onPrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', onPrompt);

    // Detect push support + whether this device is already subscribed.
    if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
      setPushSupported(true);
      navigator.serviceWorker.getRegistration().then(async (reg) => {
        try {
          const sub = reg && (await reg.pushManager.getSubscription());
          setPushEnabled(!!sub && Notification.permission === 'granted');
        } catch { /* ignore */ }
      });
    }
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      // Detach the WA manifest/meta on leave so the rest of the CRM is unaffected.
      document.querySelectorAll('[data-wa]').forEach((el) => el.remove());
    };
  }, []);

  // Home-screen icon badge: reflect total unread on the installed PWA icon.
  useEffect(() => {
    const total = conversations.reduce((n, c) => n + (c.unread_count || 0), 0);
    if ('setAppBadge' in navigator) {
      if (total > 0) navigator.setAppBadge(total).catch(() => {});
      else navigator.clearAppBadge?.().catch(() => {});
    }
  }, [conversations]);

  const fetchConversations = async () => {
    try {
      const { data } = await api.get('/whatsapp/conversations');
      setConversations(data.conversations || []);
    } catch {}
  };

  const fetchMessages = async (convId) => {
    try {
      const { data } = await api.get(`/whatsapp/conversations/${convId}/messages`);
      setMessages(data.messages || []);
    } catch {}
    try {
      const { data } = await api.get(`/whatsapp/conversations/${convId}/notes`);
      setInternalNotes(data.notes || []);
    } catch {
      setInternalNotes([]);
    }
  };

  const selectConversation = (conv) => {
    setActiveConv(conv);
    setMobileShowThread(true);
    fetchMessages(conv.id);
    // Opening a conversation clears its unread counter.
    if (conv.unread_count > 0 || conv.manually_unread) {
      api.post(`/whatsapp/conversations/${conv.id}/read`).catch(() => {});
      setConversations((prev) => prev.map((c) =>
        c.id === conv.id ? { ...c, unread_count: 0, manually_unread: false } : c));
      setActiveConv((a) => (a && a.id === conv.id ? { ...a, unread_count: 0, manually_unread: false } : a));
    }
  };

  const markUnread = async () => {
    if (!activeConv) return;
    try {
      const { data } = await api.post(`/whatsapp/conversations/${activeConv.id}/unread`);
      const n = data.unread_count || 1;
      setConversations((prev) => prev.map((c) =>
        c.id === activeConv.id ? { ...c, unread_count: n, manually_unread: true } : c));
      setActiveConv((a) => ({ ...a, unread_count: n, manually_unread: true }));
      toast.success('Marked as unread — it’ll stay flagged so it isn’t missed');
    } catch {
      toast.error('Could not mark as unread');
    }
  };

  const handleTagUser = (user) => {
    if (!taggedUsers.find((u) => u.email === user.email)) {
      setTaggedUsers([...taggedUsers, user]);
    }
    setNoteText((prev) => prev + `@${user.name} `);
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !activeConv) return;
    try {
      await api.post(`/whatsapp/conversations/${activeConv.id}/notes`, {
        text: noteText.trim(),
        tagged_users: taggedUsers.map((u) => ({ id: u.id, name: u.name, email: u.email })),
      });
      setNoteText('');
      setTaggedUsers([]);
      setShowNoteInput(false);
      fetchMessages(activeConv.id);
      toast.success('Note added');
    } catch {
      toast.error('Failed to add note');
    }
  };

  const sendMedia = async (fileArg) => {
    const file = fileArg || fileInputRef.current?.files?.[0];
    if (!file || !activeConv || attaching) return;
    const form = new FormData();
    form.append('file', file);
    form.append('caption', messageText.trim());
    if (replyTo?.id) form.append('reply_to', replyTo.id);
    form.append('signature', signature ? 'true' : 'false');
    setAttaching(true);
    try {
      await api.post(`/whatsapp/conversations/${activeConv.id}/send-media`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessageText('');
      setReplyTo(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchMessages(activeConv.id);
      fetchConversations();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === 'object' ? detail.code : null;
      if (e.response?.status === 409 || code === 'TEMPLATE_REQUIRED') {
        toast.info('The 24-hour reply window has closed — send an approved template first.');
        openTemplatePicker();
      } else {
        toast.error((typeof detail === 'object' ? detail.message : detail) || 'Could not send the file');
      }
    } finally { setAttaching(false); }
  };

  const onEmojiClick = (emojiData) => {
    setMessageText((t) => t + (emojiData.emoji || ''));
    setShowEmoji(false);
  };

  const sendMessage = async () => {
    const text = messageText.trim();
    if (!text || !activeConv || sending) return;
    setSending(true);
    try {
      await api.post(`/whatsapp/conversations/${activeConv.id}/send`, { text, reply_to: replyTo?.id || '', signature });
      setMessageText('');
      setReplyTo(null);
      await fetchMessages(activeConv.id);
      fetchConversations();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const code = typeof detail === 'object' ? detail.code : null;
      if (e.response?.status === 409 || code === 'TEMPLATE_REQUIRED') {
        toast.info('The 24-hour reply window has closed — choose an approved template.');
        openTemplatePicker();
      } else {
        toast.error((typeof detail === 'object' ? detail.message : detail) || 'Could not send message');
      }
    } finally { setSending(false); }
  };

  const openTemplatePicker = async () => {
    setTemplateOpen(true);
    setSelectedTemplate(null);
    setTemplateParams([]);
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const { data } = await api.get('/whatsapp/templates');
      setTemplates(data.templates || []);
      if (data.error) setTemplatesError(data.error);
    } catch (e) {
      setTemplatesError(e.response?.data?.detail || 'Could not load templates');
    } finally { setTemplatesLoading(false); }
  };

  const pickTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    setTemplateParams(Array.from({ length: tpl.param_count || 0 }, () => ''));
  };

  const sendTemplate = async () => {
    if (!selectedTemplate || !activeConv) return;
    if ((selectedTemplate.param_count || 0) > 0 && templateParams.some((p) => !p.trim())) {
      toast.error('Fill in all template values first');
      return;
    }
    setSendingTemplate(true);
    try {
      await api.post(`/whatsapp/conversations/${activeConv.id}/send-template`, {
        name: selectedTemplate.name,
        language_code: selectedTemplate.language || 'en_US',
        body_values: templateParams,
      });
      toast.success('Template message sent');
      setTemplateOpen(false);
      await fetchMessages(activeConv.id);
      fetchConversations();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'object' ? detail.message : detail) || 'Could not send template');
    } finally { setSendingTemplate(false); }
  };

  const importExistingChats = async () => {
    if (importing) return;
    setImporting(true);
    try {
      const { data } = await api.post('/whatsapp/sync-existing', {});
      if (data.requested) {
        toast.success(data.message || 'Import requested — chats will appear shortly.');
        setTimeout(fetchConversations, 5000);
      } else {
        const firstErr = Object.values(data.results || {}).find((r) => r && !r.ok);
        toast.error(firstErr?.error || 'Could not start the import.');
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error((typeof detail === 'object' ? detail.message : detail) || 'Could not start the import.');
    } finally { setImporting(false); }
  };

  const openSaveContact = () => {
    if (!activeConv) return;
    const existing = activeConv.contact_name && activeConv.contact_name !== activeConv.phone
      ? activeConv.contact_name : '';
    setContactNameInput(existing);
    setSaveContactOpen(true);
  };

  const saveContact = async () => {
    if (!activeConv || savingContact) return;
    const name = contactNameInput.trim();
    if (!name) { toast.error('Enter a name for the contact'); return; }
    setSavingContact(true);
    try {
      const { data } = await api.post(`/whatsapp/conversations/${activeConv.id}/save-contact`, { name });
      toast.success(`Saved ${data.name} to contacts`);
      setActiveConv((prev) => prev ? { ...prev, contact_name: data.name } : prev);
      setSaveContactOpen(false);
      fetchConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save contact');
    } finally { setSavingContact(false); }
  };

  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    const count = messages.length + internalNotes.length;
    if (count !== prevMsgCountRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      prevMsgCountRef.current = count;
    }
  }, [messages, internalNotes]);

  // Auto-refresh: silently poll the inbox and the open thread so new customer
  // replies appear without clicking Refresh.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      fetchConversations();
      if (activeConv?.id) fetchMessages(activeConv.id);
    }, 8000);
    return () => clearInterval(id);
  }, [activeConv?.id]);

  // Deep link from the daily digest email (?conv=<id>) opens that conversation.
  const deepLinkedRef = useRef(false);
  useEffect(() => {
    const convId = searchParams.get('conv');
    if (!convId || deepLinkedRef.current || conversations.length === 0) return;
    const target = conversations.find((c) => c.id === convId);
    if (target) { selectConversation(target); deepLinkedRef.current = true; }
  }, [conversations, searchParams]);

  // ─── Not Configured State ───
  if (!loading && !configured && conversations.length === 0) {
    return (
      <Layout>
        <div data-testid="whatsapp-business-page" className="flex items-center justify-center min-h-[calc(100vh-8rem)]">
          <div className="max-w-md text-center space-y-6">
            <div className="mx-auto w-20 h-20 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <MessageCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">WhatsApp Business</h1>
              <Badge className="mt-2 bg-amber-100 text-amber-700 border-amber-200">Coming Soon</Badge>
            </div>
            <p className="text-slate-500 text-sm leading-relaxed">
              Send and receive WhatsApp messages directly from Helios CRM.
              Tag team members into conversations for collaboration.
            </p>

            <div className="bg-slate-50 rounded-xl p-5 text-left space-y-3">
              <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Setup Checklist</p>
              <div className="space-y-2.5">
                {[
                  { label: 'Create Meta Business App', done: true },
                  { label: 'Add WhatsApp product to app', done: true },
                  { label: 'Verify business phone number', done: false },
                  { label: 'Configure webhook in Meta portal', done: false },
                  { label: 'Generate permanent access token', done: false },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    {step.done ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex-shrink-0" />
                    )}
                    <span className={`text-sm ${step.done ? 'text-slate-600 line-through' : 'text-slate-800'}`}>
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-emerald-50 rounded-xl p-4 text-left">
              <p className="text-xs font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
                <AtSign className="w-3.5 h-3.5" /> Team Tagging Feature
              </p>
              <p className="text-xs text-emerald-700 leading-relaxed">
                Once configured, you'll be able to @mention team members on any WhatsApp conversation
                with internal notes. Tagged users will receive a notification so nothing falls through the cracks.
              </p>
            </div>

            <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
              <Settings className="w-3.5 h-3.5" />
              <span>Configuration available in Settings once phone number is verified</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ─── Configured State — Full Messaging UI ───
  const mergedTimeline = [
    ...messages.map((m) => ({ ...m, _type: 'message' })),
    ...internalNotes.map((n) => ({ ...n, _type: 'note', timestamp: n.created_at })),
  ].sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));

  const activeConvCategory = activeConv?.category_id ? categories.find((c) => c.id === activeConv.category_id) : null;

  return (
    <Layout>
      <div data-testid="whatsapp-business-page" className="h-[calc(100vh-4rem)] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-border bg-background/95 backdrop-blur-sm">
          <div className="flex items-center gap-2 min-w-0">
            <MessageCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <h1 className="text-base sm:text-lg font-semibold truncate">WhatsApp Business</h1>
          </div>
          {/* Desktop actions */}
          <div className="hidden lg:flex items-center">
            <Button variant="outline" size="sm" onClick={fetchConversations} data-testid="wa-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" size="sm" className="ml-2" onClick={importExistingChats} disabled={importing} data-testid="wa-import-chats-btn" title="Pull your existing WhatsApp Business app contacts & chat history into the CRM">
              {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Import chats
            </Button>
            <Button size="sm" variant="outline" className="ml-2" onClick={() => setAddContactOpen(true)} data-testid="wa-add-contact-btn" title="Save a name + number to the address book">
              <UserPlus className="w-4 h-4 mr-1" /> Add contact
            </Button>
            <Button size="sm" variant="outline" className="ml-2" onClick={() => setBroadcastOpen(true)} data-testid="wa-broadcast-btn" title="Send a message to many people at once (each gets a private 1:1 chat)">
              <Megaphone className="w-4 h-4 mr-1" /> Broadcast
            </Button>
            <Button size="sm" variant="outline" className="ml-2" onClick={installToHome} data-testid="wa-add-home-btn" title="Add a one-tap WhatsApp icon to your phone's home screen">
              <Smartphone className="w-4 h-4 mr-1" /> Add to home
            </Button>
            {pushSupported && (
              pushEnabled ? (
                <Button size="sm" variant="outline" className="ml-2 border-emerald-300 text-emerald-700" onClick={sendTestPush} data-testid="wa-notify-on-btn" title="Notifications are on for this device — click to send a test">
                  <BellRing className="w-4 h-4 mr-1" /> Alerts on
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="ml-2" onClick={enablePush} disabled={pushBusy} data-testid="wa-enable-notify-btn" title="Get a notification on this device whenever a new WhatsApp message arrives">
                  {pushBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Bell className="w-4 h-4 mr-1" />} Enable alerts
                </Button>
              )
            )}
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-2" onClick={() => setComposeOpen(true)} data-testid="wa-new-message-btn">
              <Send className="w-4 h-4 mr-1" /> New message
            </Button>
          </div>
          {/* Compact actions (mobile + tablet) */}
          <div className="flex lg:hidden items-center gap-2 flex-shrink-0">
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setComposeOpen(true)} data-testid="wa-new-message-btn-mobile">
              <Send className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">New</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" data-testid="wa-more-menu-btn" title="More actions">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={fetchConversations} data-testid="wa-menu-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</DropdownMenuItem>
                <DropdownMenuItem onClick={importExistingChats} disabled={importing} data-testid="wa-menu-import"><Download className="w-4 h-4 mr-2" /> Import chats</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAddContactOpen(true)} data-testid="wa-menu-add-contact"><UserPlus className="w-4 h-4 mr-2" /> Add contact</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setBroadcastOpen(true)} data-testid="wa-menu-broadcast"><Megaphone className="w-4 h-4 mr-2" /> Broadcast</DropdownMenuItem>
                <DropdownMenuItem onClick={installToHome} data-testid="wa-menu-add-home"><Smartphone className="w-4 h-4 mr-2" /> Add to home</DropdownMenuItem>
                {pushSupported && (pushEnabled ? (
                  <>
                    <DropdownMenuItem onClick={sendTestPush} data-testid="wa-menu-notify-test"><BellRing className="w-4 h-4 mr-2" /> Send test notification</DropdownMenuItem>
                    <DropdownMenuItem onClick={disablePush} data-testid="wa-menu-notify-off"><BellRing className="w-4 h-4 mr-2" /> Turn off notifications</DropdownMenuItem>
                  </>
                ) : (
                  <DropdownMenuItem onClick={enablePush} data-testid="wa-menu-enable-notify"><Bell className="w-4 h-4 mr-2" /> Enable notifications</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Conversation List */}
          <div className={`w-full sm:w-80 lg:w-96 border-r border-border flex flex-col bg-background ${mobileShowThread ? 'hidden sm:flex' : 'flex'}`}>
            <div className="p-3 border-b border-border space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="wa-search"
                  placeholder="Search conversations..."
                  className="pl-9 h-9 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="wa-category-filter">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <SelectValue placeholder="All conversations" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="wa-filter-all">All conversations</SelectItem>
                    <SelectItem value="none" data-testid="wa-filter-none">Uncategorised</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`wa-filter-${c.id}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                          {c.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant={unreadOnly ? 'default' : 'outline'}
                  size="sm"
                  className={`h-8 px-2.5 text-xs flex-shrink-0 gap-1.5 ${unreadOnly ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                  onClick={() => setUnreadOnly((v) => !v)}
                  data-testid="wa-unread-filter-toggle"
                  title="Show only chats with unread messages"
                >
                  <span className={`w-2 h-2 rounded-full ${unreadOnly ? 'bg-white' : 'bg-emerald-500'}`} /> Unread
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8 flex-shrink-0" onClick={() => setManageOpen(true)} data-testid="wa-manage-categories-open" title="Manage categories">
                  <Palette className="w-4 h-4" />
                </Button>
                <Button
                  variant={selectMode ? 'default' : 'outline'}
                  size="icon"
                  className={`h-8 w-8 flex-shrink-0 ${selectMode ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}`}
                  onClick={() => { setSelectMode((v) => !v); setSelectedIds(new Set()); }}
                  data-testid="wa-select-toggle"
                  title="Select multiple chats to categorise together"
                >
                  <CheckSquare className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {selectMode && (
              <div className="px-3 py-2 border-b border-border bg-emerald-50 flex items-center gap-2" data-testid="wa-bulk-bar">
                <span className="text-xs font-medium text-emerald-800 flex-shrink-0" data-testid="wa-bulk-count">{selectedIds.size} selected</span>
                <Select value="" onValueChange={applyBulkCategory}>
                  <SelectTrigger className="h-8 text-xs flex-1" data-testid="wa-bulk-category">
                    <SelectValue placeholder="Tag with category…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" data-testid="wa-bulk-none">Clear category</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`wa-bulk-cat-${c.id}`}>
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                          {c.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" className="h-8 text-xs flex-shrink-0" onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }} data-testid="wa-bulk-cancel">
                  Cancel
                </Button>
              </div>
            )}
            <ScrollArea className="flex-1">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <Inbox className="w-10 h-10 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No conversations yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Messages will appear here when customers contact your WhatsApp number.
                  </p>
                </div>
              ) : (
                conversations
                  .filter((c) => {
                    if (unreadOnly && !(c.unread_count > 0 || c.manually_unread)) return false;
                    if (categoryFilter === 'none' && c.category_id) return false;
                    if (categoryFilter !== 'all' && categoryFilter !== 'none' && c.category_id !== categoryFilter) return false;
                    const q = searchQuery.toLowerCase();
                    if (!q) return true;
                    return (c.customer_name || c.contact_name || '').toLowerCase().includes(q) || (c.phone || '').includes(searchQuery);
                  })
                  .map((conv) => (
                    <ConversationItem
                      key={conv.id}
                      conv={conv}
                      isActive={activeConv?.id === conv.id}
                      onClick={() => selectConversation(conv)}
                      categories={categories}
                      selectMode={selectMode}
                      selected={selectedIds.has(conv.id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))
              )}
            </ScrollArea>
          </div>

          {/* Message Thread — full-screen overlay on mobile so the chat gets the whole screen */}
          <div className={`flex-col bg-[#efeae2] ${mobileShowThread ? 'flex fixed inset-0 z-50 sm:static sm:z-auto sm:flex-1' : 'hidden sm:flex sm:flex-1'}`}>
            {activeConv ? (
              <>
                {/* Thread header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-emerald-700 text-white">
                  <Button variant="ghost" size="icon" className="sm:hidden h-8 w-8 text-white hover:text-white/80"
                    onClick={() => setMobileShowThread(false)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="w-9 h-9 rounded-full bg-emerald-600 flex items-center justify-center">
                    <InitialsAvatar name={activeConv.customer_name || activeConv.contact_name} phone={activeConv.phone} size={36} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{activeConv.contact_name || activeConv.phone}</p>
                    <p className="text-xs text-emerald-200 flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {activeConv.phone || 'WhatsApp'}
                    </p>
                  </div>
                  {/* Desktop actions */}
                  <div className="hidden md:flex items-center gap-1">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600" data-testid="wa-category-btn" title="Set a category for this conversation">
                        {activeConvCategory ? (
                          <span className="inline-flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: activeConvCategory.color }} />
                            <span className="truncate max-w-[120px]">{activeConvCategory.label}</span>
                          </span>
                        ) : (
                          <><Tag className="w-4 h-4 mr-1" /> Category</>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-56 p-2" align="end">
                      <div className="flex items-center justify-between mb-1 px-1">
                        <span className="text-xs font-medium text-slate-500">Set category</span>
                        <button className="text-[11px] text-emerald-600 hover:underline" onClick={() => setManageOpen(true)} data-testid="wa-category-manage-link">Manage</button>
                      </div>
                      <button className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm flex items-center gap-2" onClick={() => setConversationCategory('')} data-testid="wa-category-none">
                        <X className="w-3.5 h-3.5 text-slate-400" /> None
                      </button>
                      {categories.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-2">No categories yet — tap Manage.</p>
                      ) : categories.map((c) => (
                        <button key={c.id} className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 text-sm flex items-center gap-2" onClick={() => setConversationCategory(c.id)} data-testid={`wa-category-pick-${c.id}`}>
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                          <span className="truncate">{c.label}</span>
                          {activeConv.category_id === c.id && <Check className="w-3.5 h-3.5 ml-auto text-emerald-600 flex-shrink-0" />}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                  {activeConv.customer_id ? (
                    <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600"
                      onClick={() => setLinkDialogOpen(true)} data-testid="wa-linked-customer-chip">
                      <UserCheck className="w-4 h-4 mr-1" /> {activeConv.customer_name || 'Linked'}
                    </Button>
                  ) : (
                    <>
                      <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600"
                        onClick={() => setLinkDialogOpen(true)} data-testid="wa-link-customer-btn">
                        <Link2 className="w-4 h-4 mr-1" /> {activeConv.match_suggestion ? `Link ${activeConv.match_suggestion.name}?` : 'Link to customer'}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600"
                        onClick={createCustomerFromChat} data-testid="wa-create-customer-btn" title="Create a new CRM customer pre-filled from this chat">
                        <Plus className="w-4 h-4 mr-1" /> Create customer
                      </Button>
                      <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600"
                        onClick={openSaveContact} data-testid="wa-save-contact-btn" title="Save this number as a contact (not a customer)">
                        <UserPlus className="w-4 h-4 mr-1" /> Save contact
                      </Button>
                    </>
                  )}
                  <TagUserPopover users={teamUsers} onSelect={(u) => { setShowNoteInput(true); handleTagUser(u); }}>
                    <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600" data-testid="tag-user-btn">
                      <AtSign className="w-4 h-4 mr-1" /> Tag User
                    </Button>
                  </TagUserPopover>
                  <Button variant="ghost" size="sm" className="text-white hover:text-white/80 hover:bg-emerald-600"
                    onClick={markUnread} data-testid="wa-mark-unread-btn" title="Flag this chat as unread so it isn't missed">
                    <MailPlus className="w-4 h-4 mr-1" /> Mark unread
                  </Button>
                  </div>
                  {/* Mobile actions overflow menu */}
                  <div className="md:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:text-white/80 hover:bg-emerald-600" data-testid="wa-thread-more-btn" title="Actions">
                          <MoreVertical className="w-5 h-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {activeConv.customer_id ? (
                          <DropdownMenuItem onClick={() => setLinkDialogOpen(true)} data-testid="wa-menu-linked"><UserCheck className="w-4 h-4 mr-2" /> {activeConv.customer_name || 'Linked customer'}</DropdownMenuItem>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => setLinkDialogOpen(true)} data-testid="wa-menu-link"><Link2 className="w-4 h-4 mr-2" /> {activeConv.match_suggestion ? `Link ${activeConv.match_suggestion.name}?` : 'Link to customer'}</DropdownMenuItem>
                            <DropdownMenuItem onClick={createCustomerFromChat} data-testid="wa-menu-create-customer"><Plus className="w-4 h-4 mr-2" /> Create customer</DropdownMenuItem>
                            <DropdownMenuItem onClick={openSaveContact} data-testid="wa-menu-save-contact"><UserPlus className="w-4 h-4 mr-2" /> Save contact</DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger data-testid="wa-menu-category">
                            <Tag className="w-4 h-4 mr-2" />
                            {activeConvCategory ? activeConvCategory.label : 'Category'}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-52">
                            <DropdownMenuItem onClick={() => setConversationCategory('')} data-testid="wa-menu-category-none"><X className="w-3.5 h-3.5 mr-2 text-slate-400" /> None</DropdownMenuItem>
                            {categories.map((c) => (
                              <DropdownMenuItem key={c.id} onClick={() => setConversationCategory(c.id)} data-testid={`wa-menu-category-${c.id}`}>
                                <span className="w-3 h-3 rounded-full mr-2 flex-shrink-0" style={{ backgroundColor: c.color }} />
                                <span className="truncate">{c.label}</span>
                                {activeConv.category_id === c.id && <Check className="w-3.5 h-3.5 ml-auto text-emerald-600" />}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setManageOpen(true)} data-testid="wa-menu-category-manage"><Palette className="w-3.5 h-3.5 mr-2" /> Manage categories</DropdownMenuItem>
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger data-testid="wa-menu-tag-user"><AtSign className="w-4 h-4 mr-2" /> Tag a colleague</DropdownMenuSubTrigger>
                          <DropdownMenuSubContent className="w-52 max-h-64 overflow-y-auto">
                            {(teamUsers || []).length === 0 ? (
                              <DropdownMenuItem disabled>No team members</DropdownMenuItem>
                            ) : teamUsers.map((u) => (
                              <DropdownMenuItem key={u.id || u.email} onClick={() => { setShowNoteInput(true); handleTagUser(u); }} data-testid={`wa-menu-tag-${u.id || u.email}`}>
                                {u.name || u.email}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={markUnread} data-testid="wa-menu-mark-unread"><MailPlus className="w-4 h-4 mr-2" /> Mark unread</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                <ReplyWindowTimer lastInboundAt={activeConv.last_inbound_at} />
                <LeadFormBanner conversationId={activeConv.id} linkedCustomerId={activeConv.customer_id}
                  onLinked={(d) => {
                    setActiveConv((a) => (a ? { ...a, customer_id: d.customer_id, customer_name: d.customer_name, contact_name: d.customer_name, match_suggestion: null } : a));
                    fetchConversations();
                    fetchMessages(activeConv.id);
                  }} />

                {/* Messages + Notes timeline */}
                <ScrollArea className="flex-1 py-3">
                  {mergedTimeline.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <MessageCircle className="w-8 h-8 text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">No messages yet</p>
                    </div>
                  ) : (
                    mergedTimeline.map((item) =>
                      item._type === 'note' ? (
                        <InternalNote key={item.id} note={item} />
                      ) : (
                        <MessageBubble key={item.id} msg={item} onReply={setReplyTo} />
                      )
                    )
                  )}
                  <div ref={messagesEndRef} />
                </ScrollArea>

                {/* Internal Note Input */}
                {showNoteInput && (
                  <div className="p-3 border-t border-border bg-amber-50">
                    <div className="flex items-center gap-2 mb-2">
                      <StickyNote className="w-4 h-4 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800">Internal Note</span>
                      {taggedUsers.length > 0 && (
                        <div className="flex items-center gap-1 ml-2">
                          {taggedUsers.map((u) => (
                            <Badge key={u.email} variant="outline" className="text-[10px] h-5 bg-amber-100 border-amber-300">
                              @{u.name}
                              <button onClick={() => setTaggedUsers(taggedUsers.filter((t) => t.email !== u.email))} className="ml-1">
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </Badge>
                          ))}
                        </div>
                      )}
                      <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={() => { setShowNoteInput(false); setNoteText(''); setTaggedUsers([]); }}>
                        Cancel
                      </Button>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="flex-1 relative">
                        <Textarea
                          data-testid="note-input"
                          placeholder="Write an internal note... Use @mention to tag users"
                          className="min-h-[60px] text-sm bg-white"
                          value={noteText}
                          onChange={(e) => setNoteText(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <TagUserPopover users={teamUsers} onSelect={handleTagUser}>
                          <Button variant="outline" size="icon" className="h-8 w-8" data-testid="note-tag-btn">
                            <AtSign className="w-4 h-4" />
                          </Button>
                        </TagUserPopover>
                        <Button size="icon" className="h-8 w-8 bg-amber-600 hover:bg-amber-700" onClick={handleAddNote} data-testid="add-note-btn">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Message Compose */}
                <div className="p-3 border-t border-border bg-background">
                  {replyTo && (
                    <div className="flex items-center gap-2 mb-2 rounded-md bg-slate-100 border-l-2 border-emerald-500 px-3 py-1.5" data-testid="wa-reply-bar">
                      <Reply className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-medium text-slate-500">
                          Replying to {replyTo.direction === 'outbound' ? 'yourself' : (activeConv.contact_name || 'them')}
                        </p>
                        <p className="text-xs text-slate-600 truncate">{replyTo.body || replyTo.text || (replyTo.media_kind ? `[${replyTo.media_kind}]` : 'message')}</p>
                      </div>
                      <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => setReplyTo(null)} data-testid="wa-reply-cancel">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,video/*"
                    className="hidden"
                    data-testid="wa-file-input"
                    onChange={(e) => { if (e.target.files?.[0]) sendMedia(e.target.files[0]); }}
                  />
                  <label className="flex items-center gap-2 mb-2 text-xs text-muted-foreground cursor-pointer w-fit" data-testid="wa-signature-toggle">
                    <Checkbox checked={signature} onCheckedChange={(v) => setSignature(!!v)} data-testid="wa-signature-checkbox" />
                    Add signature (your name + “Helios Electrical and Solar”)
                  </label>
                  <div className="flex items-end gap-2">
                    {/* Mobile: compact "+" menu keeps the input + send always visible */}
                    <div className="sm:hidden">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" data-testid="wa-mobile-plus-btn" title="More options">
                            <Plus className="w-5 h-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" side="top" className="w-56">
                          <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="wa-menu-attach"><Paperclip className="w-4 h-4 mr-2" /> Attach photo / file</DropdownMenuItem>
                          <DropdownMenuItem onClick={openTemplatePicker} data-testid="wa-menu-template"><FileText className="w-4 h-4 mr-2" /> Template message</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setShowNoteInput(true)} data-testid="wa-menu-note"><StickyNote className="w-4 h-4 mr-2" /> Internal note</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => wrapSelection('*')} data-testid="wa-menu-bold"><Bold className="w-4 h-4 mr-2" /> Bold text</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => wrapSelection('_')} data-testid="wa-menu-italic"><Italic className="w-4 h-4 mr-2" /> Italic text</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => wrapSelection('~')} data-testid="wa-menu-strike"><Strikethrough className="w-4 h-4 mr-2" /> Strikethrough</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => wrapSelection('```')} data-testid="wa-menu-mono"><Code className="w-4 h-4 mr-2" /> Monospace</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger data-testid="wa-menu-quick"><Zap className="w-4 h-4 mr-2" /> Quick replies</DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="w-60 max-h-64 overflow-y-auto">
                              {quickReplies.length === 0 ? (
                                <DropdownMenuItem disabled>No quick replies</DropdownMenuItem>
                              ) : quickReplies.map((qr) => (
                                <DropdownMenuItem key={qr.id} onClick={() => insertQuickReply(qr)} data-testid={`wa-menu-qr-${qr.id}`}>
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{qr.title}</div>
                                    <div className="text-[11px] text-slate-500 truncate">{qr.text}</div>
                                  </div>
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setQrManageOpen(true)} data-testid="wa-menu-qr-manage"><Pencil className="w-3.5 h-3.5 mr-2" /> Manage</DropdownMenuItem>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {/* Desktop tools */}
                    <div className="hidden sm:flex items-end gap-2">
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" onClick={() => setShowNoteInput(!showNoteInput)} data-testid="toggle-note-btn" title="Add internal note with @mentions">
                      <StickyNote className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" onClick={() => fileInputRef.current?.click()} disabled={attaching} data-testid="wa-attach-btn" title="Send a photo, PDF or short video">
                      {attaching ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                    </Button>
                    <Popover open={showEmoji} onOpenChange={setShowEmoji}>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" data-testid="wa-emoji-btn" title="Insert emoji">
                          <Smile className="w-5 h-5" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" side="top" className="p-0 w-auto border-none bg-transparent shadow-none">
                        <EmojiPicker onEmojiClick={onEmojiClick} width={320} height={380} previewConfig={{ showPreview: false }} />
                      </PopoverContent>
                    </Popover>
                    <Popover open={qrPopoverOpen} onOpenChange={setQrPopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" data-testid="wa-quick-reply-btn" title="Insert a saved quick reply">
                          <Zap className="w-5 h-5" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="start" side="top" className="w-72 p-2">
                        <div className="flex items-center justify-between mb-1 px-1">
                          <span className="text-xs font-medium text-slate-500">Quick replies</span>
                          <button className="text-[11px] text-emerald-600 hover:underline" onClick={() => { setQrPopoverOpen(false); setQrManageOpen(true); }} data-testid="wa-qr-manage-link">Manage</button>
                        </div>
                        <div className="max-h-64 overflow-y-auto space-y-0.5">
                          {quickReplies.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-3">No quick replies yet — tap Manage to add one.</p>
                          ) : quickReplies.map((qr) => (
                            <button
                              key={qr.id}
                              onClick={() => insertQuickReply(qr)}
                              className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-100"
                              data-testid={`wa-qr-item-${qr.id}`}
                            >
                              <div className="text-sm font-medium text-slate-800 truncate">{qr.title}</div>
                              <div className="text-[11px] text-slate-500 truncate">{qr.text}</div>
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" onClick={() => wrapSelection('*')} data-testid="wa-bold-btn" title="Bold — wraps text in *asterisks*">
                      <Bold className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" onClick={() => wrapSelection('_')} data-testid="wa-italic-btn" title="Italic — wraps text in _underscores_">
                      <Italic className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" onClick={() => wrapSelection('~')} data-testid="wa-strike-btn" title="Strikethrough — wraps text in ~tildes~">
                      <Strikethrough className="w-5 h-5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-10 w-10 text-muted-foreground" onClick={() => wrapSelection('```')} data-testid="wa-mono-btn" title="Monospace — wraps text in ```backticks```">
                      <Code className="w-5 h-5" />
                    </Button>
                    </div>
                    <div className="relative flex-1 min-w-0">
                      <div
                        ref={mirrorRef}
                        aria-hidden="true"
                        className="absolute inset-0 z-0 overflow-hidden whitespace-pre-wrap break-words rounded-md border border-transparent px-3 py-2 text-base md:text-sm leading-snug text-slate-900 pointer-events-none"
                        style={{ wordBreak: 'break-word' }}
                        dangerouslySetInnerHTML={{ __html: waMarkupHtml(messageText) + (messageText.endsWith('\n') ? '&nbsp;' : '') }}
                      />
                      <Textarea
                        ref={messageInputRef}
                        data-testid="wa-message-input"
                        placeholder="Type a message... (Enter for a new line)"
                        rows={1}
                        className="relative z-10 w-full resize-none max-h-[50vh] py-2 leading-snug bg-transparent text-transparent caret-slate-900"
                        value={messageText}
                        onChange={(e) => {
                          setMessageText(e.target.value);
                          const el = e.target;
                          el.style.height = 'auto';
                          el.style.height = Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.5)) + 'px';
                          if (mirrorRef.current) mirrorRef.current.scrollTop = el.scrollTop;
                        }}
                        onScroll={(e) => { if (mirrorRef.current) mirrorRef.current.scrollTop = e.target.scrollTop; }}
                      />
                    </div>
                    <Button variant="outline" size="sm" className="hidden sm:inline-flex flex-shrink-0" onClick={openTemplatePicker} data-testid="wa-template-btn" title="Send an approved template message">
                      Template
                    </Button>
                    <Button size="icon" className="bg-emerald-600 hover:bg-emerald-700 flex-shrink-0" onClick={sendMessage} disabled={sending || !messageText.trim()} data-testid="wa-send-btn">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4">
                  <MessageCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-medium text-base mb-1">WhatsApp Business</h3>
                <p className="text-sm text-muted-foreground max-w-xs">
                  Select a conversation to view messages, reply, and tag team members.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <LinkCustomerDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        conv={activeConv}
        onLinked={(data) => {
          setActiveConv((prev) => prev ? { ...prev, customer_id: data.customer_id, customer_name: data.customer_name, contact_name: data.customer_name, match_suggestion: null } : prev);
          fetchConversations();
        }}
      />

      <WhatsAppBroadcastDialog open={broadcastOpen} onOpenChange={setBroadcastOpen} />

      <Dialog open={addContactOpen} onOpenChange={setAddContactOpen}>
        <DialogContent data-testid="wa-add-contact-dialog">
          <DialogHeader>
            <DialogTitle>Add contact</DialogTitle>
            <DialogDescription>Save a name and number to the address book. When a customer with this number is added, their chat links automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Full name" value={newContact.name}
              onChange={(e) => setNewContact((c) => ({ ...c, name: e.target.value }))} data-testid="wa-add-contact-name" />
            <Input placeholder="Phone number (e.g. 447700900123)" value={newContact.phone}
              onChange={(e) => setNewContact((c) => ({ ...c, phone: e.target.value }))} data-testid="wa-add-contact-phone" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddContactOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={saveNewContact} disabled={savingNewContact} data-testid="wa-add-contact-save">
              {savingNewContact ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <WhatsAppComposeDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        onSent={() => fetchConversations()}
      />

      <Dialog open={saveContactOpen} onOpenChange={setSaveContactOpen}>
        <DialogContent className="max-w-sm" data-testid="wa-save-contact-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus className="w-4 h-4" /> Save as contact</DialogTitle>
            <DialogDescription>Add this WhatsApp number to your shared address book.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Save <span className="font-medium">{activeConv?.phone}</span> to your shared address book
              (hello@helioses.co.uk) so the name shows on staff phones. This is a contact, not a customer.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-slate-600">Contact name</label>
              <Input
                autoFocus
                data-testid="wa-save-contact-name"
                placeholder="e.g. Dave the Roofer"
                value={contactNameInput}
                onChange={(e) => setContactNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') saveContact(); }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveContactOpen(false)}>Cancel</Button>
            <Button onClick={saveContact} disabled={savingContact || !contactNameInput.trim()}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-save-contact-submit">
              {savingContact ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Save contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={templateOpen} onOpenChange={setTemplateOpen}>
        <DialogContent className="max-w-lg" data-testid="wa-template-dialog">
          <DialogHeader>
            <DialogTitle>Send a template message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-slate-500">
              Outside the 24-hour reply window, WhatsApp only allows pre-approved templates. Pick one below.
            </p>
            {templatesLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : templatesError ? (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="wa-template-error">{templatesError}</div>
            ) : templates.length === 0 ? (
              <div className="rounded-lg bg-slate-50 border p-4 text-sm text-slate-500 text-center">
                No approved templates found in your WhatsApp Business Account yet. Create &amp; get one approved in Meta Business Manager, then it'll appear here.
              </div>
            ) : !selectedTemplate ? (
              <div className="space-y-2" data-testid="wa-template-list">
                {templates.map((t) => (
                  <button key={`${t.name}-${t.language}`} onClick={() => pickTemplate(t)}
                    className="w-full text-left p-3 rounded-lg border hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
                    data-testid={`wa-template-${t.name}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm text-slate-800">{t.name}</span>
                      <Badge variant="outline" className="text-[10px]">{t.language} · {t.category}</Badge>
                    </div>
                    {t.body_text && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.body_text}</p>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-3" data-testid="wa-template-selected">
                <div className="p-3 rounded-lg border bg-slate-50">
                  <p className="font-medium text-sm text-slate-800">{selectedTemplate.name}</p>
                  {selectedTemplate.body_text && <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{selectedTemplate.body_text}</p>}
                </div>
                {(selectedTemplate.param_count || 0) > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-slate-600">Fill in the template values</p>
                    {templateParams.map((val, i) => (
                      <Input key={i} placeholder={`Value for {{${i + 1}}}`} value={val}
                        onChange={(e) => setTemplateParams((prev) => prev.map((p, idx) => (idx === i ? e.target.value : p)))}
                        data-testid={`wa-template-param-${i}`} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {selectedTemplate && (
              <Button variant="ghost" onClick={() => setSelectedTemplate(null)} data-testid="wa-template-back">Back</Button>
            )}
            <Button onClick={sendTemplate} disabled={!selectedTemplate || sendingTemplate}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-template-send">
              {sendingTemplate ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-md" data-testid="wa-manage-categories-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Palette className="w-4 h-4" /> Manage categories</DialogTitle>
            <DialogDescription>Create your own labels and colours to organise conversations.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {categories.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">No categories yet — add one below.</p>
              ) : categories.map((c) => (
                <div key={c.id} className="flex items-center gap-2 p-2 rounded-lg border" data-testid={`wa-cat-row-${c.id}`}>
                  <span className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                  <span className="text-sm flex-1 truncate">{c.label}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCatForm({ id: c.id, label: c.label, color: c.color })} data-testid={`wa-cat-edit-${c.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-600" onClick={() => deleteCategory(c.id)} data-testid={`wa-cat-delete-${c.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs font-medium text-slate-600">{catForm.id ? 'Edit category' : 'New category'}</p>
              <Input placeholder="Category name (e.g. Awaiting contract)" value={catForm.label}
                onChange={(e) => setCatForm((f) => ({ ...f, label: e.target.value }))} data-testid="wa-cat-name-input" />
              <div className="flex items-center gap-1.5 flex-wrap">
                {CATEGORY_COLORS.map((col) => (
                  <button key={col} type="button" onClick={() => setCatForm((f) => ({ ...f, color: col }))}
                    className={`w-6 h-6 rounded-full transition-transform ${catForm.color === col ? 'ring-2 ring-offset-2 ring-slate-400 scale-110' : ''}`}
                    style={{ backgroundColor: col }} data-testid={`wa-cat-color-${col}`} />
                ))}
              </div>
              <div className="flex gap-2 items-center">
                {catForm.id && (
                  <Button variant="ghost" size="sm" onClick={() => setCatForm({ id: '', label: '', color: '#2563eb' })} data-testid="wa-cat-cancel-edit">Cancel</Button>
                )}
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-auto" onClick={saveCategory} disabled={savingCat} data-testid="wa-cat-save">
                  {savingCat ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (catForm.id ? <Check className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />)}
                  {catForm.id ? 'Save changes' : 'Add category'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={qrManageOpen} onOpenChange={setQrManageOpen}>
        <DialogContent className="max-w-md" data-testid="wa-qr-manage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="w-4 h-4" /> Quick replies</DialogTitle>
            <DialogDescription>Save canned messages your team can drop into a reply in one tap. Use <code>{'{name}'}</code> to auto-insert the customer's first name.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {quickReplies.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-2">No quick replies yet — add one below.</p>
              ) : quickReplies.map((qr) => (
                <div key={qr.id} className="flex items-start gap-2 p-2 rounded-lg border" data-testid={`wa-qr-row-${qr.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{qr.title}</div>
                    <div className="text-[11px] text-slate-500 line-clamp-2">{qr.text}</div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0" onClick={() => setQrForm({ id: qr.id, title: qr.title, text: qr.text })} data-testid={`wa-qr-edit-${qr.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0 text-rose-500 hover:text-rose-600" onClick={() => deleteQuickReply(qr.id)} data-testid={`wa-qr-delete-${qr.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs font-medium text-slate-600">{qrForm.id ? 'Edit quick reply' : 'New quick reply'}</p>
              <Input placeholder="Short title (e.g. Quote sent)" value={qrForm.title}
                onChange={(e) => setQrForm((f) => ({ ...f, title: e.target.value }))} data-testid="wa-qr-title-input" />
              <Textarea placeholder="Message text… (use {name} for the customer's first name)" rows={3} value={qrForm.text}
                onChange={(e) => setQrForm((f) => ({ ...f, text: e.target.value }))} data-testid="wa-qr-text-input" />
              <div className="flex gap-2 items-center">
                {qrForm.id && (
                  <Button variant="ghost" size="sm" onClick={() => setQrForm({ id: '', title: '', text: '' })} data-testid="wa-qr-cancel-edit">Cancel</Button>
                )}
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 ml-auto" onClick={saveQuickReply} disabled={savingQr} data-testid="wa-qr-save">
                  {savingQr ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : (qrForm.id ? <Check className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />)}
                  {qrForm.id ? 'Save changes' : 'Add quick reply'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={iosHelpOpen} onOpenChange={setIosHelpOpen}>
        <DialogContent className="max-w-sm" data-testid="wa-ios-help-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Smartphone className="w-4 h-4" /> Add to Home Screen</DialogTitle>
            <DialogDescription>Get a one-tap icon that opens straight into WhatsApp.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-600">
            <p className="font-medium text-slate-700">On iPhone / iPad (Safari):</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Tap the <span className="font-medium">Share</span> button (square with an up arrow).</li>
              <li>Scroll down and tap <span className="font-medium">Add to Home Screen</span>.</li>
              <li>Tap <span className="font-medium">Add</span> — the Helios WA icon appears on your home screen.</li>
            </ol>
            <p className="font-medium text-slate-700 pt-1">On Android (Chrome):</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Tap the <span className="font-medium">⋮</span> menu (top right).</li>
              <li>Tap <span className="font-medium">Add to Home screen / Install app</span>.</li>
            </ol>
          </div>
          <DialogFooter>
            <Button onClick={() => setIosHelpOpen(false)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-ios-help-close">Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
